import { ref } from 'vue';

// Global shared state - singleton pattern
const maximizedPopups = ref(new Set());

/**
 * Composable for detecting collision between mini popup and maximized popups
 * Used to automatically adjust mini popup position when large popups open
 */
export function usePopupCollisionDetector() {
  /**
   * Check if mini popup collides with footer button area of maximized popups
   * @param {HTMLElement} miniElement - The mini popup element
   * @returns {boolean} True if collision detected
   */
  const checkCollisionWithMiniPopup = (miniElement) => {
    // No collision if no maximized popup is open
    const hasMaximizedPopup = maximizedPopups.value.size > 0;
    if (!hasMaximizedPopup || !miniElement) return false;

    // Get mini popup position
    const miniRect = miniElement.getBoundingClientRect();

    // Try to find actual footer element for more accurate detection
    const footerElement = document.querySelector(
      '.modal__footer, .footer-buttons'
    );
    let footerHeight = 56; // Default fallback

    if (footerElement) {
      const footerRect = footerElement.getBoundingClientRect();
      footerHeight = footerRect.height + 20; // Add 20px padding
    }

    const footerAreaTop = window.innerHeight - footerHeight;

    // Check if mini popup overlaps with footer area
    return (
      miniRect.bottom > footerAreaTop && // Mini popup extends into footer area
      miniRect.right > 0 && // Has horizontal overlap
      miniRect.left < window.innerWidth
    );
  };

  /**
   * Register a maximized popup
   * @param {string} popupId - Unique identifier for the popup
   */
  const registerMaximizedPopup = (popupId) => {
    maximizedPopups.value.add(popupId);
  };

  /**
   * Unregister a maximized popup
   * @param {string} popupId - Unique identifier for the popup
   */
  const unregisterMaximizedPopup = (popupId) => {
    maximizedPopups.value.delete(popupId);
  };

  /**
   * Check if any maximized popup is currently open
   * @returns {boolean}
   */
  const hasMaximizedPopups = () => {
    return maximizedPopups.value.size > 0;
  };

  return {
    maximizedPopups,
    checkCollisionWithMiniPopup,
    registerMaximizedPopup,
    unregisterMaximizedPopup,
    hasMaximizedPopups,
  };
}
